import React from 'react';
import type { FormikProps } from '@axiom/ui';
import {
  Card,
  CardHeader,
  CardSection,
  Checkbox,
  DropdownTree,
  FormGroup,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Layout,
  SmallHeader,
  TaxonomyUtil,
  useBreakpoint,
} from '@axiom/ui';
import type { ProjectNeedsWithLegalSkills, Taxonomy } from '@axiom/validation';

type ExpertiseValues = Partial<ProjectNeedsWithLegalSkills>;

type SkillGroups = ReturnType<typeof TaxonomyUtil.getFocusAreaSkillGroups>;

export type DirectEngagementsFormExpertiseCardProps = {
  filteredSkillGroups: SkillGroups;
  setFilteredSkillGroups: (skillGroups: SkillGroups) => void;
  setValues: FormikProps<ExpertiseValues>['setValues'];
  taxonomy: Taxonomy;
  values: ExpertiseValues;
};

export const DirectEngagementsFormExpertiseCard = ({
  filteredSkillGroups,
  setFilteredSkillGroups,
  setValues,
  taxonomy,
  values,
}: DirectEngagementsFormExpertiseCardProps) => {
  const { isMobile } = useBreakpoint();
  const { focusAreas, legalSkills, practiceAreas, skillGroups } = taxonomy;
  const PracticeAreasWithFocusAreaOptions =
    TaxonomyUtil.getPracticeAreasWithFocusAreaOptions(
      focusAreas,
      practiceAreas
    );

  return (
    <Card>
      <CardHeader>
        <SmallHeader>
          What type of legal experience are you looking for?
        </SmallHeader>
      </CardHeader>
      <CardSection>
        <Grid>
          <GridRow gutterBottom="24px">
            <GridColumn width={12}>
              <DropdownTree
                name="primaryFocusArea"
                label="Primary subject matter expertise"
                onChange={async value => {
                  const focusArea =
                    typeof value === 'string' ? value : undefined;
                  // Remove the values for the question(s) if focus area changes
                  const legalSkillsAnswers: string[][] = [];

                  await setValues({
                    ...values,
                    legalSkillsAnswers,
                    primaryFocusArea: focusArea,
                    otherFocusAreas: values.otherFocusAreas
                      ? values.otherFocusAreas.filter(ofa => ofa !== focusArea)
                      : undefined,
                  });
                  setFilteredSkillGroups(
                    TaxonomyUtil.getFocusAreaSkillGroups(
                      focusArea ?? '',
                      skillGroups
                    )
                  );
                }}
                options={PracticeAreasWithFocusAreaOptions}
                nestedKey="choices"
                displayKey="label"
                valueKey="value"
                parentOperatesAsDropdownOnly
              />
              {values.primaryFocusArea
                ? filteredSkillGroups.map(({ key, clientQuestion }, index) => {
                    const legalSkillOptions = TaxonomyUtil.getLegalSkillOptions(
                      legalSkills,
                      key
                    );
                    return (
                      <Gutter top="24px" key={key}>
                        <FormGroup
                          name={`legalSkillsAnswers.${index}`}
                          label={clientQuestion}
                        >
                          <Layout
                            horizontalGutter="8px"
                            verticalGutter="8px"
                            stackableOn="mobile"
                            wrap
                          >
                            {legalSkillOptions.map(({ label, value }) => (
                              <Checkbox
                                mode={isMobile ? 'checkbox' : 'pill'}
                                name={`legalSkillsAnswers.${index}`}
                                option={value}
                                displayValue={label}
                                key={value}
                              />
                            ))}
                          </Layout>
                        </FormGroup>
                      </Gutter>
                    );
                  })
                : null}
            </GridColumn>
          </GridRow>
          <GridRow>
            <GridColumn width={12}>
              <DropdownTree
                parentOperatesAsDropdownOnly
                name="otherFocusAreas"
                label="Additional subject matter expertise"
                options={PracticeAreasWithFocusAreaOptions.map(
                  practiceAreaWithFocusOptions => {
                    return {
                      ...practiceAreaWithFocusOptions,
                      choices: practiceAreaWithFocusOptions.choices.filter(
                        choice => choice.value !== values.primaryFocusArea
                      ),
                    };
                  }
                )}
                nestedKey="choices"
                displayKey="label"
                valueKey="value"
              />
            </GridColumn>
          </GridRow>
        </Grid>
      </CardSection>
    </Card>
  );
};
